import type { ChartConfig } from './chart-utils';
import { formatValue } from './chart-utils';

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    dataKey: string;
    color: string;
    payload: Record<string, unknown>;
  }>;
  label?: string;
  config: ChartConfig;
  unit?: string;
  /** Abweichende Einheit fuer bestimmte Keys (z.B. rechte Y-Achse bei ComposedChart) */
  unitRight?: string;
  /** Keys, die `unitRight` statt `unit` verwenden */
  rightKeys?: string[];
  labelFormatter?: (label: string) => string;
  /** Erzwungene Nachkommastellen. `undefined` = auto (0–2 Stellen). */
  decimals?: number;
}

/**
 * ChartTooltip - Einheitlicher Tooltip fuer alle Chart-Typen.
 * Zeigt Label, Farbe und Wert passend zur ChartConfig.
 */
export function ChartTooltipContent({
  active,
  payload,
  label,
  config,
  unit,
  unitRight,
  rightKeys,
  labelFormatter,
  decimals,
}: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const displayLabel = labelFormatter ? labelFormatter(label ?? '') : label;

  return (
    <div className="chart-tooltip">
      {displayLabel && (
        <p className="chart-tooltip-label text-h5">{displayLabel}</p>
      )}
      <div className="chart-tooltip-items">
        {payload.map((entry) => {
          // Kartesische Serien tragen den Config-Key in `dataKey` (z.B. <Bar
          // dataKey="strom">). Pie/Radial nutzen `dataKey="value"` für alle
          // Segmente — die Segment-Identität (der Config-Key) steckt dann in
          // `name` (der nameKey-Wert). Erst dataKey, dann name auflösen.
          const configEntry = config[entry.dataKey] ?? config[entry.name];
          // Hover darf einen eigenen Text führen (z.B. ohne das Prozent-Suffix,
          // das in der Legende steht): tooltipLabel hat Vorrang, sonst label.
          const displayName = configEntry?.tooltipLabel ?? configEntry?.label ?? entry.name;
          const effectiveUnit = rightKeys?.includes(entry.dataKey) ? unitRight : unit;
          return (
            <div key={entry.dataKey} className="chart-tooltip-item">
              <span
                className="chart-tooltip-dot"
                style={{ backgroundColor: entry.color }}
              />
              <span className="chart-tooltip-name text-ui-700 text-h5">{displayName}</span>
              <span className="chart-tooltip-value text-h4">
                {formatValue(entry.value, { unit: effectiveUnit, decimals })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
